/**
 * Utility functions for validating content for inappropriate or obscene words
 * Used in form validations across the application
 */

// List of obscene words to check against
// This list should be maintained and updated as needed
const obsceneWords = [
  "xvideos", "xvideos.com",
  "xnxx", "xnxx.com",
  "pornhub", "pornhub.com",
  "redtube", "redtube.com",
  "youporn", "youporn.com",
  "xhamster", "xhamster.com",
  "brazzers", "brazzers.com",
  "onlyfans", "onlyfans.com",
  "xart", "xart.com",
  "spankbang", "spankbang.com",
  "hentaiverse", "hentaiverse.com",
  "hentaihaven", "hentaihaven.com",
  "rule34", "rule34.xxx",
  "fuckbook", "fuckbook.com",
  "cam4", "cam4.com",
  "camsoda", "camsoda.com",
  "chaturbate", "chaturbate.com",
  "livejasmin", "livejasmin.com",
  "nudogram", "nudogram.com",
  "bangbros", "bangbros.com",
  "fleshlight", "fleshlight.com",
  "nud3", "nud3.com",
  "desirehub", "desirehub.com",
  "adultfriendfinder", "adultfriendfinder.com",

  // Palavras genéricas que costumam aparecer em links
  "porn", "sexy", "sex", "nude", "xxx", "camgirl", "porra", "cuzao", "cuzinho", "camsex", "porn", "pornor",
  "amadoras", "analporn", "gostosa", "gostosona", "pelada", "escort", "milf",
  "porn", "porno", "pornô", "xvideos", "xnxx", "youporn", "redtube", "xhamster",
  "brazzers", "onlyfans", "spankbang", "hentai", "rule34", "camgirl", "camsex",
  "chaturbate", "nudogram", "cam4", "camsoda", "livejasmin", "fuckbook", "nudes",
  "nude", "pelada", "pelado", "gozada", "gozar", "boquete", "foda", "fodase",
  "pica", "pau", "rola", "rolaum", "rolao", "buceta", "bct", "cu", "cú", "mamilo",
  "mamilos", "peitos", "mama", "tesao", "tesão", "safada", "safado", "puta",
  "puto", "putaria", "anal", "oral", "69", "deepthroat", "creampie", "cumshot",
  "fetiche", "bdsm", "orgasmo", "masturba", "milf", "pornostar", "escort",
  "vibrador", "zoofilia", "necrofilia", "incesto", "transar", "hardcore", "nud3",
  "shemale", "sexo", "sex0", "s3xo", "x-art", "xart", "vagina", "penis", "pênis",

  // Ofensivas gerais
  "caralho", "cacete", "merda", "bosta", "desgraça", "arrombado", "viado", "phoda", "foda-se",
  "viadinho", "gayzinho", "boiola", "bicha", "traveco", "sapatão", "gorda",
  "gordo", "retardado", "imbecil", "burro", "otário", "otaria", "corno", "vagabunda",
  "vagabundo", "nojenta", "nojento", "desgraçado", "lixo", "escroto", "escrota",
  "tapado", "mongol", "mongoloide", "deficiente", "aleijado", "aleijada", "gay", "yag",

  // Racismo e xenofobia
  "macaco", "preto", "negro","xxx", "escravo", "escrava", "zé povinho", "favelado",
  "pardal", "índio", "índia", "bugre", "cigano", "batedor de carteira", "moreno nojento",

  // Xenofobia e outras ofensas culturais
  "baiano burro", "nordestino preguiçoso", "paraíba", "chaveiro", "argentino lixo",
  "estrangeiro ladrão", "china", "chinês do vírus", "coronga", "coroninha",

  // Criativas e camufladas
  "p0rn", "b0quete", "s3xo", "p1ca", "saf4da", "put@", "goz4da", "bct", "c0rno", "g@y", "y@g",
  "f0da", "f0dase", "r0la", "f3tiche", "analzinho", "rol4", "buz3ta", "buc3ta", "v4gina"

];

/**
 * Checks if a text contains any obscene words
 * @param text - The text to check
 * @returns An object with validation result and the found obscene word if any
 */
export const containsObsceneWords = (text: string): { isValid: boolean; foundWord?: string } => {
  if (!text) return { isValid: true };

  const lowerText = text.toLowerCase();

  for (const word of obsceneWords) {
    if (lowerText.includes(word.toLowerCase())) {
      return { isValid: false, foundWord: word };
    }
  }

  return { isValid: true };
};

/**
 * Validates a URL for obscene words in its path or domain
 * @param url - The URL to validate
 * @returns An object with validation result and the found obscene word if any
 */
export const validateUrlContent = (url: string): { isValid: boolean; foundWord?: string } => {
  if (!url) return { isValid: true };

  try {
    // Extract domain and path from URL
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname;

    // Check domain
    const domainResult = containsObsceneWords(domain);
    if (!domainResult.isValid) {
      return domainResult;
    }

    // Check path
    const pathResult = containsObsceneWords(path);
    if (!pathResult.isValid) {
      return pathResult;
    }

    return { isValid: true };
  } catch (error) {
    // If URL is invalid, just check the whole string
    return containsObsceneWords(url);
  }
};

/**
 * Validates form fields for obscene content
 * Can be used with Zod schema validation
 * @param fieldName - The name of the field being validated (for error messages)
 * @returns A validation function compatible with Zod's refine method
 */
export const createObsceneContentValidator = (fieldName: string) => {
  return (value: string) => {
    const result = containsObsceneWords(value);
    return {
      success: result.isValid,
      error: result.isValid ? undefined : `O campo ${fieldName} contém a palavra inapropriada "${result.foundWord}"`
    };
  };
};

/**
 * Validates URL fields for obscene content
 * Can be used with Zod schema validation
 * @param fieldName - The name of the field being validated (for error messages)
 * @returns A validation function compatible with Zod's refine method
 */
export const createUrlContentValidator = (fieldName: string) => {
  return (value: string) => {
    if (!value) return { success: true };

    const result = validateUrlContent(value);
    return {
      success: result.isValid,
      error: result.isValid ? undefined : `A URL no campo ${fieldName} contém a palavra inapropriada "${result.foundWord}"`
    };
  };
};
